/**
 * Gerenciador de downloads como recurso da aplicação.
 *
 * Uma instância só, viva enquanto o app estiver aberto: dois gerenciadores
 * consumindo a mesma fila baixariam o mesmo arquivo duas vezes.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/db/schema';
import { DownloadManager, type DownloadErrorKind } from '@/services/downloads/DownloadManager';
import { useMedia } from '@/features/media/MediaProvider';
import type { DownloadJob } from '@/db/schema';
import type { Sign } from '@/types/catalog';

export interface DownloadFailureNotice {
  signId: string;
  kind: DownloadErrorKind;
  message: string;
}

interface DownloadContextValue {
  jobs: DownloadJob[] | undefined;
  paused: boolean;
  lastFailure: DownloadFailureNotice | undefined;
  canDownload: (sign: Sign) => boolean;
  queueSign: (sign: Sign) => Promise<boolean>;
  queueCategory: (categoryId: string) => Promise<number>;
  queueAll: () => Promise<number>;
  cancel: (signId: string) => Promise<void>;
  cancelAll: () => Promise<void>;
  pause: () => void;
  resume: () => void;
  dismissFailure: () => void;
}

const DownloadContext = createContext<DownloadContextValue | null>(null);

export function DownloadProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [paused, setPaused] = useState(false);
  const [lastFailure, setLastFailure] = useState<DownloadFailureNotice | undefined>(undefined);
  const [, forceRefresh] = useState(0);

  // Resolvedor compartilhado: ele já carregou o manifest da origem, então o
  // gerenciador sabe exatamente o que pode ser baixado.
  const { resolver } = useMedia();

  const managerRef = useRef<DownloadManager | null>(null);
  if (!managerRef.current) {
    managerRef.current = new DownloadManager(resolver, {
      onFailed: (signId, kind, message) => setLastFailure({ signId, kind, message }),
      onQueueChanged: () => forceRefresh((value) => value + 1),
    });
  }
  const manager = managerRef.current;

  // A fila vem do banco, não de estado em memória: é a mesma fonte que sobrevive
  // ao fechamento do app.
  const jobs = useLiveQuery(
    async () => (await db.downloadQueue.toArray()).sort((a, b) => a.queuedAt.localeCompare(b.queuedAt)),
    [],
  );

  useEffect(() => {
    // Retoma o que estava pendente ao abrir o app.
    void manager.start().catch(() => undefined);

    /**
     * No iOS a PWA é suspensa ao ir para segundo plano e os downloads param. Ao
     * voltar, é preciso retomar explicitamente — não há garantia de continuidade
     * em background (PLAN §18).
     */
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void manager.start().catch(() => undefined);
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      manager.dispose();
    };
  }, [manager]);

  const pause = useCallback(() => {
    manager.pause();
    setPaused(true);
  }, [manager]);

  const resume = useCallback(() => {
    manager.resume();
    setPaused(false);
  }, [manager]);

  const value = useMemo<DownloadContextValue>(
    () => ({
      jobs,
      paused,
      lastFailure,
      canDownload: (sign) => resolver.canDownload(sign),
      queueSign: (sign) => manager.queueSign(sign),
      queueCategory: (categoryId) => manager.queueCategory(categoryId),
      queueAll: () => manager.queueAll(),
      cancel: (signId) => manager.cancel(signId),
      cancelAll: () => manager.cancelAll(),
      pause,
      resume,
      dismissFailure: () => setLastFailure(undefined),
    }),
    [jobs, paused, lastFailure, manager, resolver, pause, resume],
  );

  return <DownloadContext.Provider value={value}>{children}</DownloadContext.Provider>;
}

export function useDownloads(): DownloadContextValue {
  const context = useContext(DownloadContext);
  if (!context) throw new Error('useDownloads precisa estar dentro de <DownloadProvider>');
  return context;
}
